import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useIsNarrow, gridCols } from './ui';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

const C = {
  overdue: '#A63A3A',
  pending: '#c9a84c',
  done:    '#2D4A3E',
  navy:    '#1a2744',
};

function KpiCard({ label, value, accent, sub, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      style={{
        backgroundColor: '#fff', borderRadius: '10px', padding: '18px 20px',
        boxShadow: hovered ? '0 0 0 1.5px #c9a84c' : '0 1px 3px rgba(0,0,0,0.05)',
        borderTop: '0.5px solid #EEE', borderRight: '0.5px solid #EEE',
        borderBottom: '0.5px solid #EEE', borderLeft: `3px solid ${accent}`,
        transition: 'box-shadow 0.12s', cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <p style={{ fontSize: '11px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0', fontWeight: 600 }}>{label}</p>
      <p style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 4px 0', color: accent }}>{value ?? '-'}</p>
      <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>{sub}</p>
    </div>
  );
}

export default function HRReport({ onNavigate }) {
  const [summary,              setSummary]              = useState(null);
  const [incompleteOnboarding, setIncompleteOnboarding] = useState([]);
  const [visaRenewals,         setVisaRenewals]         = useState([]);
  const [missingDocs,          setMissingDocs]          = useState([]);
  const [loading,              setLoading]              = useState(true);
  const [error,                setError]                = useState(null);
  const isNarrow = useIsNarrow();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/hr/report/summary`),
      axios.get(`${API}/hr/report/incomplete-onboarding`),
      axios.get(`${API}/hr/report/visa-renewals`),
      axios.get(`${API}/hr/report/missing-documents`),
    ])
      .then(([s, inc, visa, docs]) => {
        setSummary(s.data);
        setIncompleteOnboarding(inc.data);
        setVisaRenewals(visa.data);
        setMissingDocs(docs.data);
      })
      .catch(() => setError('Failed to load HR report data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '40px', color: '#aaa', fontSize: '14px' }}>Loading HR report...</div>;
  if (error)   return <div style={{ padding: '40px', color: C.overdue, fontSize: '14px' }}>{error}</div>;

  // One row per employee × missing document (so each row shows name + single doc type)
  const flatMissing = missingDocs.flatMap(row =>
    row.missing_documents.map(doc => ({ key: `${row.employee_id}-${doc}`, name: row.name, doc_type: doc }))
  );

  return (
    <div style={{ margin: '-20px -24px', padding: '24px 24px 0', background: '#edecea' }}>
      <h2 style={{ fontSize: '16px', fontWeight: 600, color: C.navy, margin: '0 0 20px 0' }}>HR Report</h2>

      {/* ROW 1 - 4 KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, 'repeat(4, 1fr)'), gap: '14px', marginBottom: '20px' }}>
        <KpiCard
          label="Total active employees"
          value={summary?.total_active}
          accent={C.navy}
          sub="current headcount"
          onClick={() => onNavigate?.('employees', '/employees')}
        />
        <KpiCard
          label="Visa sponsored"
          value={summary?.visa_sponsored}
          accent={C.done}
          sub="active visa employees"
          onClick={() => onNavigate?.('employees', '/employees?visa_required=true', { visaFilter: true })}
        />
        <KpiCard
          label="Incomplete onboarding"
          value={summary?.incomplete_onboarding}
          accent={C.pending}
          sub="forms with missing sections"
        />
        <KpiCard
          label="Documents expiring soon"
          value={summary?.documents_expiring_soon}
          accent={C.overdue}
          sub="within 90 days"
        />
      </div>

      {/* ROW 2 - left / right */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, '1fr 1fr'), gap: '16px', alignItems: 'start' }}>

        {/* LEFT - Incomplete onboarding */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Incomplete onboarding</h3>
          {incompleteOnboarding.length === 0 ? (
            <p style={{ fontSize: '13px', color: C.done, fontWeight: 600, margin: 0 }}>✓ All onboarding forms complete</p>
          ) : incompleteOnboarding.map(emp => (
            <div key={emp.employee_id} style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              padding: '8px 10px', marginBottom: '6px', backgroundColor: '#f9f8f6', borderRadius: '6px',
            }}>
              <div style={{ flex: 1, minWidth: 0, marginRight: '10px' }}>
                <div style={{ marginBottom: '5px' }}>
                  <span style={{ fontWeight: 600, fontSize: '13px', color: C.navy }}>{emp.name}</span>
                  <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>{emp.entity}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {emp.missing_sections.map(section => (
                    <span key={section} style={{
                      fontSize: '10px', fontWeight: 600, borderRadius: '4px', padding: '1px 6px',
                      backgroundColor: emp.any_done ? '#fdf8ee' : '#fdf5f5',
                      color:           emp.any_done ? '#7a4d00' : '#7a1f1f',
                    }}>
                      {section}
                    </span>
                  ))}
                </div>
              </div>
              <span style={{
                fontSize: '10px', fontWeight: 600, borderRadius: '10px', padding: '2px 8px',
                flexShrink: 0, whiteSpace: 'nowrap', alignSelf: 'flex-start',
                backgroundColor: emp.any_done ? '#fdf8ee' : '#fdf5f5',
                color:           emp.any_done ? C.pending : C.overdue,
                border:         `0.5px solid ${emp.any_done ? C.pending : C.overdue}`,
              }}>
                {emp.any_done ? 'In progress' : 'Not started'}
              </span>
            </div>
          ))}
        </div>

        {/* RIGHT - 2 stacked cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Card A - Upcoming visa renewals */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>Upcoming visa renewals: next 6 months</h3>
            {visaRenewals.length === 0 ? (
              <p style={s.empty}>No upcoming visa renewals</p>
            ) : visaRenewals.map(row => {
              const urgent = row.days_remaining < 60;
              const accent = urgent ? C.overdue : C.pending;
              return (
                <div key={row.employee_id} style={{
                  display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '6px',
                  borderRadius: '6px', borderLeft: `3px solid ${accent}`,
                  backgroundColor: urgent ? '#fdf5f5' : '#fdf8ee',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: C.navy }}>{row.name}</span>
                    <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>
                      {toDisplayDate(row.visa_expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: accent, marginLeft: '8px', flexShrink: 0 }}>
                    {row.days_remaining}d
                  </span>
                </div>
              );
            })}
          </div>

          {/* Card B - Missing documents */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>Missing documents</h3>
            <p style={{ fontSize: '11px', color: '#aaa', margin: '-10px 0 12px 0' }}>
              Required: Passport · CV · Proof of Address
            </p>
            {flatMissing.length === 0 ? (
              <p style={{ fontSize: '13px', color: C.done, fontWeight: 600, margin: 0 }}>✓ All documents uploaded</p>
            ) : flatMissing.map(item => (
              <div key={item.key} style={{
                display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '4px',
                borderRadius: '6px', borderLeft: `3px solid ${C.overdue}`, backgroundColor: '#fdf5f5',
              }}>
                <span style={{ fontWeight: 600, fontSize: '13px', color: C.navy, flex: 1 }}>{item.name}</span>
                <span style={{ fontSize: '12px', color: '#888', flexShrink: 0 }}>{item.doc_type}</span>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

const s = {
  card:      { backgroundColor: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '0.5px solid #EEE' },
  cardTitle: { fontSize: '14px', fontWeight: 600, color: '#1a2744', margin: '0 0 14px 0' },
  empty:     { fontSize: '13px', color: '#667085', fontStyle: 'italic', margin: '8px 0 0 0' },
};
